/**
 * Extracts business card information from OCR text
 * @param {string} text - Raw OCR text
 * @returns {Object} Extracted data (firstName, lastName, email, phone, etc.)
 */
export const extractBusinessCardInfo = (text) => {
    const extracted = {};

    // Helper to clean a line of extreme noise
    const cleanLine = (line) => {
        // Remove common OCR noise characters at start/end
        return line.replace(/^[^a-zA-Z0-9+]+|[^a-zA-Z0-9)]+$/g, '').trim();
    };

    // Split into lines and clean them
    const lines = text.split('\n')
        .map(l => cleanLine(l))
        .filter(l => l.length > 2); // Filter out very short lines

    // --- 1. EMAIL (High confidence) ---
    // Look for email pattern anywhere in the text
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z]{2,})/gi;
    const emailMatch = text.match(emailRegex);
    if (emailMatch) {
        extracted.email = emailMatch[0].toLowerCase();
    }

    // --- 2. WEBSITE ---
    // Look for website pattern anywhere
    const websiteRegex = /(?:https?:\/\/)?(?:www\.)?[a-zA-Z0-9-]+\.(?:com|be|fr|net|org|io|eu|info)/gi;
    const websiteMatch = text.match(websiteRegex);
    if (websiteMatch) {
        // Filter out emails
        const validWebsites = websiteMatch.filter(w => !w.includes('@'));
        if (validWebsites.length > 0) {
            let website = validWebsites[0].toLowerCase();
            // Remove common OCR noise prefix if attached (e.g. "j www.")
            website = website.replace(/^[^a-z0-9]+/, '');

            if (!website.startsWith('http')) {
                website = 'https://' + website;
            }
            extracted.website = website;
        }
    }

    // --- 3. PHONE ---
    // Look for phone patterns. 
    // Example input: "Lait +32472964 034 Ra"
    // We want to capture "+32472964 034"
    const phoneRegex = /(?:(?:\+|00)\d{1,3}[\s.-]?)?(?:(?:\(\d{1,4}\))|(?:\d{1,4}))[\s.-]?\d{2,4}[\s.-]?\d{2,4}[\s.-]?\d{0,4}/g;
    const phoneMatches = text.match(phoneRegex);
    if (phoneMatches) {
        // Filter: must have at least 8 digits
        const validPhones = phoneMatches.filter(p => p.replace(/\D/g, '').length >= 8);
        if (validPhones.length > 0) {
            // Sort by length (descending) to get the most complete number
            validPhones.sort((a, b) => b.length - a.length);
            // Clean up the result
            extracted.phone = validPhones[0].replace(/[^\d+]/g, ' ').trim().replace(/\s+/g, ' ');
        }
    }

    // --- 4. NAME ---
    // Strategy: Look for lines with 2-3 words that are mostly uppercase.
    // Context clue: The line BEFORE a job title is often the name.

    // First, identify the line index of the position if found
    let positionLineIndex = -1;
    const positionKeywords = [
        'fondateur', 'co-fondateur', 'gérant', 'gerant', 'directeur', 'director',
        'manager', 'responsable', 'chef', 'président', 'president', 'ceo', 'cto', 'cfo',
        'developer', 'développeur', 'consultant', 'commercial', 'sales', 'marketing'
    ];

    for (let i = 0; i < lines.length; i++) {
        const lowerLine = lines[i].toLowerCase();
        if (positionKeywords.some(keyword => lowerLine.includes(keyword))) {
            positionLineIndex = i;
            // Extract position while we are here
            let cleanPos = lines[i].replace(/^[^a-zA-ZÀ-Ü]+/, '').replace(/[^a-zA-ZÀ-Ü)]+$/, '');
            extracted.position = cleanPos;
            break;
        }
    }

    // If we found a position, check the line before it for the name
    if (positionLineIndex > 0) {
        const candidateLine = lines[positionLineIndex - 1];
        // Clean noise like "122 — " or " F5"
        // Keep only letters, spaces, and hyphens
        const cleanName = candidateLine.replace(/[^a-zA-ZÀ-Ü\s-]/g, '').trim();
        const words = cleanName.split(/\s+/);

        if (words.length >= 2 && words.length <= 4) {
            extracted.firstName = words[0];
            extracted.lastName = words.slice(1).join(' ');
        }
    }

    // If no name found via context, fall back to scanning for uppercase lines
    if (!extracted.firstName) {
        const forbidden = [
            'STATS', 'STAT', 'INFO', 'TEL', 'FAX', 'GSM', 'BE', 'SA', 'SPRL', 'SRL', 'NV', 'BV', 'LTD',
            'TVA', 'EMAIL', 'WEB', 'SITE', 'RUE', 'AVENUE', 'BOULEVARD'
        ];

        for (const line of lines) {
            // Skip lines with email/website/numbers
            if (line.includes('@') || line.includes('www.') || line.includes('http') || /\d/.test(line)) continue;

            // Extract potential name parts (words with only letters/accents)
            const words = line.match(/[A-ZÀ-Ü]{2,}/g);

            if (words && words.length >= 2) {
                const cleanWords = words.filter(w => !forbidden.includes(w) && w.length > 2);

                if (cleanWords.length >= 2) {
                    extracted.firstName = cleanWords[0];
                    extracted.lastName = cleanWords.slice(1).join(' ');
                    break;
                }
            }
        }
    }

    // --- 5. POSITION (if not already found) ---
    if (!extracted.position) {
        for (const line of lines) {
            const lowerLine = line.toLowerCase();
            if (positionKeywords.some(keyword => lowerLine.includes(keyword))) {
                let cleanPos = line.replace(/^[^a-zA-ZÀ-Ü]+/, '').replace(/[^a-zA-ZÀ-Ü)]+$/, '');
                extracted.position = cleanPos;
                break;
            }
        }
    }

    // --- 6. COMPANY ---
    const companyKeywords = [
        'brasserie', 'restaurant', 'agence', 'société', 'company', 'group', 'groupe',
        'sarl', 'sa', 'sprl', 'srl', 'nv', 'bv', 'inc', 'ltd', 'solutions', 'services',
        'studio', 'atelier', 'bureau'
    ];

    for (const line of lines) {
        // Skip if it's the name we just found
        if (extracted.firstName && line.includes(extracted.firstName)) continue;
        // Skip email/website
        if (line.includes('@') || line.includes('www.')) continue;

        const lowerLine = line.toLowerCase();
        if (companyKeywords.some(keyword => lowerLine.includes(keyword)) && !extracted.company) {
            // Clean the line
            // Example: "es BRASSERIE DES BEAUX JOURS = 224" -> "BRASSERIE DES BEAUX JOURS"
            // Strategy: Find the keyword, take everything around it that looks like words

            // Remove common noise start/end
            let cleanComp = line.replace(/^[^a-zA-ZÀ-Ü]+/, '').replace(/[^a-zA-ZÀ-Ü]+$/, '');

            // If it contains "=" or numbers at the end, strip them
            cleanComp = cleanComp.replace(/\s*=\s*\d+.*$/, '');

            extracted.company = cleanComp;
            break;
        }
    }

    // --- 7. ADDRESS ---
    const addressKeywords = [
        'rue', 'avenue', 'boulevard', 'av.', 'bd.', 'place', 'chaussée', 'route',
        'street', 'road', 'lane', 'drive', 'way'
    ];

    for (const line of lines) {
        if (addressKeywords.some(keyword => line.toLowerCase().includes(keyword))) {
            extracted.address = line;
            break;
        }
    }

    return extracted;
};
